/**
 * PortfolioFilters Component
 * TASK-207: Portfolio Level View - Enhanced Filters
 *
 * Main container for all portfolio-level filters
 * Includes: View Level Selector, Accounts, Asset Class, Currency, and Date filters
 *
 * Designed to be reusable across:
 * - Portfolio Level View (single portfolio deep dive)
 * - Client Level View (multi-portfolio comparison)
 */

import React from 'react';
import { Filter, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  PortfolioFilters as PortfolioFiltersType,
  AccountFilter,
  ViewLevel,
} from '../../../types/portfolio-views';
import Badge from '../atoms/Badge';
import ViewLevelSelector, { ClientOption, PortfolioOption } from './ViewLevelSelector';
import AccountsFilter from './AccountsFilter';
import AssetClassFilter from './AssetClassFilter';
import CurrencyFilter from './CurrencyFilter';
import DateFilter from './DateFilter';
import {
  useViewContext,
  useNavigationActions,
} from '../../../services/portfolio-refactor/store/portfolioStore';

interface PortfolioFiltersProps {
  filters: PortfolioFiltersType;
  availableAccounts: AccountFilter[];
  onFiltersChange: (filters: Partial<PortfolioFiltersType>) => void;
  compact?: boolean;
  historicalSupported?: boolean;
  minHistoricalDate?: Date;
  // For ViewLevelSelector dropdowns
  clients?: ClientOption[];
  portfolios?: PortfolioOption[];
  // Control which filters to show based on view level
  showAccountsFilter?: boolean;
  showAssetClassFilter?: boolean;
  showCurrencyFilter?: boolean;
  showDateFilter?: boolean;
}

const PortfolioFilters: React.FC<PortfolioFiltersProps> = ({
  filters,
  availableAccounts,
  onFiltersChange,
  compact = false,
  historicalSupported = false,
  minHistoricalDate,
  clients = [],
  portfolios = [],
  showAccountsFilter = true,
  showAssetClassFilter = true,
  showCurrencyFilter = true,
  showDateFilter = true,
}) => {
  const navigate = useNavigate();
  const viewContext = useViewContext();
  const { drillDown: _drillDown, drillUp: _drillUp, setViewContext } = useNavigationActions();

  // Handle view level change with optional ID selection
  const handleViewLevelChange = (level: ViewLevel, id?: string) => {
    // Navigate based on target level
    if (level === 'my-book') {
      // Update view context
      setViewContext({ level: 'my-book', clientId: undefined, portfolioId: undefined });
      navigate('/portfolio/my-book');
    } else if (level === 'client') {
      // If ID provided, use it; otherwise use current context
      const clientId = id || viewContext.clientId;
      if (clientId) {
        // Update view context with client ID
        setViewContext({ level: 'client', clientId, portfolioId: undefined });
        navigate(`/portfolio/client/${clientId}`);
      }
    } else if (level === 'portfolio') {
      // If ID provided, use it; otherwise use current context
      const portfolioId = id || viewContext.portfolioId;
      const clientId = viewContext.clientId; // Keep current client context
      if (portfolioId && clientId) {
        setViewContext({ level: 'portfolio', clientId, portfolioId });
        navigate(`/portfolio/client/${clientId}/portfolio/${portfolioId}`);
      }
    }
  };

  // Calculate active filter count (excluding view level - that's navigation)
  const getActiveCount = (): number => {
    let count = 0;

    // Asset classes (exclude 'all')
    if (!filters.assetClasses.includes('all')) {
      count += filters.assetClasses.length;
    }

    // Currencies (exclude 'all')
    if (!filters.currencies.includes('all')) {
      count += filters.currencies.length;
    }

    // Accounts (if any disabled)
    const disabledAccounts = filters.accounts.filter((acc) => !acc.enabled);
    if (disabledAccounts.length > 0 && disabledAccounts.length < filters.accounts.length) {
      count += 1;
    }

    // Date mode (if historical)
    if (filters.dateMode === 'historical') {
      count += 1;
    }

    return count;
  };

  const activeCount = getActiveCount();

  // Reset all filters to defaults
  const handleReset = () => {
    onFiltersChange({
      accounts: availableAccounts.map((acc) => ({ ...acc, enabled: true })),
      assetClasses: ['all'],
      currencies: ['all'],
      dateMode: 'latest',
      historicalDate: undefined,
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          {activeCount > 0 && (
            <Badge
              label={`${activeCount} active`}
              variant="info"
              size="sm"
            />
          )}
        </div>
        {activeCount > 0 && (
          <button
            onClick={handleReset}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            <X className="w-4 h-4" />
            Reset Filters
          </button>
        )}
      </div>

      {/* Filter Grid - Improved Spacing and Responsiveness */}
      <div className="space-y-6">
        {/* View Level Selector - Full Width on its own row to prevent overlap */}
        <div className="w-full">
          <ViewLevelSelector
            currentLevel={viewContext.level}
            onLevelChange={handleViewLevelChange}
            clientId={viewContext.clientId}
            portfolioId={viewContext.portfolioId}
            clients={clients}
            portfolios={portfolios}
          />
        </div>

        {/* Other Filters in Responsive Grid */}
        {/* Calculate grid columns based on visible filters */}
        {(() => {
          const visibleFiltersCount = [
            showAccountsFilter,
            showAssetClassFilter,
            showCurrencyFilter,
            showDateFilter,
          ].filter(Boolean).length;

          // If no filters to show, don't render the grid
          if (visibleFiltersCount === 0) return null;

          // Dynamic grid class based on number of visible filters
          const gridClass = compact
            ? 'grid-cols-1 lg:grid-cols-2'
            : visibleFiltersCount === 1
            ? 'grid-cols-1'
            : visibleFiltersCount === 2
            ? 'grid-cols-1 sm:grid-cols-2'
            : visibleFiltersCount === 3
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4';

          return (
            <div className={`grid gap-6 ${gridClass}`}>
          {/* Accounts Filter */}
          {showAccountsFilter && (
            <div>
              <AccountsFilter
                accounts={filters.accounts.length > 0 ? filters.accounts : availableAccounts}
                onChange={(accounts) => onFiltersChange({ accounts })}
              />
            </div>
          )}

          {/* Asset Class Filter */}
          {showAssetClassFilter && (
            <div>
              <AssetClassFilter
                selected={filters.assetClasses}
                onChange={(assetClasses) => onFiltersChange({ assetClasses })}
              />
            </div>
          )}

          {/* Currency Filter */}
          {showCurrencyFilter && (
            <div>
              <CurrencyFilter
                selected={filters.currencies}
                onChange={(currencies) => onFiltersChange({ currencies })}
              />
            </div>
          )}

          {/* Date Filter */}
          {showDateFilter && (
            <div>
              <DateFilter
                dateMode={filters.dateMode}
                historicalDate={filters.historicalDate}
                onChange={(dateMode, historicalDate) =>
                  onFiltersChange({ dateMode, historicalDate })
                }
                historicalSupported={historicalSupported}
                minDate={minHistoricalDate}
              />
            </div>
          )}
            </div>
          );
        })()}
      </div>

      {/* Active Filters Summary (optional, for better UX) */}
      {activeCount > 0 && !compact && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Active filters:{' '}
            {!filters.assetClasses.includes('all') && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-1">
                {filters.assetClasses.length} Asset {filters.assetClasses.length === 1 ? 'Class' : 'Classes'}
              </span>
            )}
            {!filters.currencies.includes('all') && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-1">
                {filters.currencies.length} {filters.currencies.length === 1 ? 'Currency' : 'Currencies'}
              </span>
            )}
            {filters.accounts.some((acc) => !acc.enabled) && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-1">
                {filters.accounts.filter((acc) => acc.enabled).length} of {filters.accounts.length} Accounts
              </span>
            )}
            {filters.dateMode === 'historical' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-1">
                Historical: {filters.historicalDate}
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default PortfolioFilters;

// Re-export types for external use
export type { ClientOption, PortfolioOption } from './ViewLevelSelector';
